import type { CanvasLayoutBox } from '@/lib/transforms/types/canvas-layout-box'

/**
 * Snap axis-aligned box edges under `transform: scale(canvasZoom)` (origin top-left) so shape
 * shells and selection chrome share the same pixel grid **without growing** the logical box:
 * min edges use `ceil` and max edges use `floor` in device space, which pulls each edge inward
 * by at most ~1 CSS pixel total per axis (tighter than symmetric `round`, which often expands).
 */
export function snapLayoutBoxToCanvasZoomGrid(
  box: CanvasLayoutBox,
  canvasZoom: number
): CanvasLayoutBox {
  const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1
  const z = canvasZoom * dpr

  if (!Number.isFinite(z) || z <= 0) {
    return { ...box }
  }

  const x1 = box.x
  const y1 = box.y
  const x2 = box.x + box.width
  const y2 = box.y + box.height
  let rx1 = Math.ceil(x1 * z) / z
  let ry1 = Math.ceil(y1 * z) / z
  let rx2 = Math.floor(x2 * z) / z
  let ry2 = Math.floor(y2 * z) / z

  // Degenerate after inward snap (tiny geometry / extreme zoom): fall back to symmetric snap.
  if (rx2 <= rx1) {
    rx1 = Math.round(x1 * z) / z
    rx2 = Math.round(x2 * z) / z
  }

  if (ry2 <= ry1) {
    ry1 = Math.round(y1 * z) / z
    ry2 = Math.round(y2 * z) / z
  }

  return {
    x: rx1,
    y: ry1,
    width: Math.max(0, rx2 - rx1),
    height: Math.max(0, ry2 - ry1),
  }
}
